import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCardModule } from '@angular/material/card';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import {
  DataTableMultiSelectFilterComponent,
  DataTableMultiSelectFilterOption,
} from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { AssignmentEditorComponent, AssignmentEditorDialogData } from '../assignment-editor/assignment-editor.component';
import { AssignmentManagementService, AssignmentDto, UpdateAssignmentDto } from '../../services/assignment-management.service';
import { AreaDto, AreaManagementService } from '../../services/area-management.service';

const UNASSIGNED_LEVEL_FILTER_VALUE = '__sin_nivel_configurado__';

/**
 * Componente de listado de nombramientos
 * Muestra una tabla con los nombramientos disponibles y acciones para gestionar
 */
@Component({
  selector: 'app-assignment-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatSlideToggleModule,
    MatCardModule,
    PageHeaderComponent,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
  ],
  templateUrl: './assignment-list.component.html',
  styleUrl: './assignment-list.component.scss'
})
export class AssignmentListComponent implements OnInit, OnDestroy {
  readonly organizationLevelOptions = [
    { value: 1, label: 'Iglesia Regional' },
    { value: 2, label: 'Area Apostol' },
    { value: 3, label: 'Distrito' },
    { value: 4, label: 'Grupo de Comunidad' },
    { value: 5, label: 'Comunidad' },
  ];

  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true, editable: true, editType: 'text' },
    { key: 'area', header: 'Area', hideable: true, visible: true, resizable: true, editable: true, editType: 'select', editOptions: [] },
    {
      key: 'levels',
      header: 'Niveles',
      hideable: true,
      visible: true,
      resizable: true,
      editable: true,
      editType: 'multiSelect',
      editOptions: this.organizationLevelOptions,
      editValue: row => this.getAssignmentLevelIds(row)
    },
    {
      key: 'requiresActiveMinistry',
      header: 'Ministerio',
      hideable: true,
      visible: true,
      resizable: true,
      editable: true,
      editType: 'select',
      editOptions: [
        { value: 'true', label: 'Requiere' },
        { value: 'false', label: 'No requiere' },
      ]
    },
    { key: 'isActive', header: 'Activo', hideable: true, visible: true, resizable: true },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false }
  ];
  tableData: AssignmentDto[] = [];
  isLoading = false;
  enableDragDrop = true;
  savingCell?: { row: AssignmentDto; column: string };
  activeFilterChips: FilterChip[] = [];
  areaOptions: AreaDto[] = [];
  levelFilterOptions: string[] = [];
  areaSelectOptions: DataTableMultiSelectFilterOption[] = [];
  levelSelectOptions: DataTableMultiSelectFilterOption[] = [];
  allAssignments: AssignmentDto[] = [];
  readonly statusFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' },
  ];
  readonly ministryFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'required', label: 'Requiere' },
    { value: 'not-required', label: 'No requiere' },
  ];
  readonly filterForm = new FormGroup({
    searchTerm: new FormControl('', { nonNullable: true }),
    status: new FormControl<string[]>(['active'], { nonNullable: true }),
    areaId: new FormControl<string[]>([], { nonNullable: true }),
    level: new FormControl<string[]>([], { nonNullable: true }),
    requiresActiveMinistry: new FormControl<string[]>(
      this.ministryFilterOptions.map(option => option.value),
      { nonNullable: true },
    ),
  });

  private destroy$ = new Subject<void>();
  private areaFilterInitialized = false;
  private levelFilterInitialized = false;

  constructor(
    private service: AssignmentManagementService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private areaService: AreaManagementService
  ) {}

  ngOnInit(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(200),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyFilter());

    this.loadAreas();
    this.loadAssignments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga todos los nombramientos
   */
  private loadAssignments(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignments: AssignmentDto[]) => {
          this.allAssignments = assignments ?? [];
          this.isLoading = false;
          this.cdr.markForCheck();
          this.refreshFilterOptions();
          this.applyFilter();
        },
        error: (error: any) => {
          console.error('Error loading assignments:', error);
          this.notificationService.error('Error al cargar los nombramientos');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Aplica los filtros a la tabla
   */
  private applyFilter(): void {
    const filters = this.filterForm.getRawValue();
    const searchTerm = filters.searchTerm.trim().toLowerCase();
    
    let filteredData = this.allAssignments;
    
    if (filters.status.length === 0 || filters.areaId.length === 0 || filters.level.length === 0 || filters.requiresActiveMinistry.length === 0) {
      filteredData = [];
    } else {
      filteredData = filteredData.filter(assignment => {
        const levels = assignment.allowedOrganizationLevelNames ?? [];
        const matchesLevel = levels.length === 0
          ? filters.level.includes(UNASSIGNED_LEVEL_FILTER_VALUE)
          : levels.some(level => filters.level.includes(level));

        return filters.status.includes(assignment.isActive ? 'active' : 'inactive') &&
          filters.areaId.includes(String(assignment.areaId)) &&
          matchesLevel &&
          filters.requiresActiveMinistry.includes(assignment.requiresActiveMinistry ? 'required' : 'not-required');
      });
    }

    if (searchTerm) {
      filteredData = filteredData.filter(assignment => this.matchesSearch(assignment, searchTerm));
    }
    
    this.tableData = [...filteredData];
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  private matchesSearch(assignment: AssignmentDto, searchTerm: string): boolean {
    const searchable = [
      assignment.name,
      assignment.areaName,
      ...(assignment.allowedOrganizationLevelNames ?? [])
    ];

    return searchable.some(value => String(value ?? '').toLowerCase().includes(searchTerm));
  }

  private buildFilterChips(): void {
    const chips: FilterChip[] = [];
    const filters = this.filterForm.getRawValue();
    this.addMultiSelectChip(chips, 'status', 'Estado', filters.status, this.statusFilterOptions);
    this.addMultiSelectChip(chips, 'areaId', 'Área', filters.areaId, this.areaSelectOptions);
    this.addMultiSelectChip(chips, 'level', 'Nivel', filters.level, this.levelSelectOptions);
    this.addMultiSelectChip(chips, 'requiresActiveMinistry', 'Ministerio', filters.requiresActiveMinistry, this.ministryFilterOptions);

    this.activeFilterChips = chips;
  }

  private buildAreaSelectOptions(): DataTableMultiSelectFilterOption[] {
    const areas = new Map<string, string>();
    this.allAssignments.forEach(assignment => {
      if (assignment.areaId != null) {
        areas.set(String(assignment.areaId), assignment.areaName ?? `Área ${assignment.areaId}`);
      }
    });

    return [...areas.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }

  private getUniqueLevels(): string[] {
    return Array.from(new Set(
      this.allAssignments
        .flatMap(assignment => assignment.allowedOrganizationLevelNames ?? [])
        .filter((level): level is string => typeof level === 'string')
    )).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }

  private refreshFilterOptions(): void {
    const configuredLevels = this.getUniqueLevels();
    const hasAssignmentsWithoutLevels = this.allAssignments.some(assignment =>
      (assignment.allowedOrganizationLevelNames ?? []).length === 0);
    this.levelFilterOptions = hasAssignmentsWithoutLevels
      ? [...configuredLevels, UNASSIGNED_LEVEL_FILTER_VALUE]
      : configuredLevels;
    this.areaSelectOptions = this.buildAreaSelectOptions();
    this.levelSelectOptions = this.levelFilterOptions.map(level => ({
      value: level,
      label: level === UNASSIGNED_LEVEL_FILTER_VALUE ? 'Sin nivel asignado' : level,
    }));
    const areaValues = this.areaSelectOptions.map(option => option.value);

    if (!this.areaFilterInitialized && areaValues.length > 0) {
      this.areaFilterInitialized = true;
      this.filterForm.controls.areaId.setValue(areaValues, { emitEvent: false });
    }
    if (!this.levelFilterInitialized && this.levelFilterOptions.length > 0) {
      this.levelFilterInitialized = true;
      this.filterForm.controls.level.setValue(this.levelFilterOptions, { emitEvent: false });
    }

    this.setAreaEditOptions();
  }

  private addMultiSelectChip(
    chips: FilterChip[],
    key: string,
    label: string,
    values: readonly string[],
    options: readonly DataTableMultiSelectFilterOption[],
  ): void {
    if (values.length === options.length) {
      return;
    }

    const selection = values.length === 0
      ? 'Ninguno'
      : values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');
    chips.push({ key, label: `${label}: ${selection}`, removable: true });
  }

  private getAssignmentLevelIds(assignment: AssignmentDto): number[] {
    if (assignment.allowedOrganizationLevelIds?.length) {
      return assignment.allowedOrganizationLevelIds;
    }

    return (assignment.allowedOrganizationLevelNames ?? [])
      .map(name => this.organizationLevelOptions.find(level => level.label === name)?.value)
      .filter((id): id is number => id != null);
  }

  private getLevelNames(levelIds: number[]): string[] {
    return levelIds
      .map(id => this.organizationLevelOptions.find(level => level.value === id)?.label)
      .filter((name): name is string => !!name);
  }

  private areNumberArraysEqual(first: number[], second: number[]): boolean {
    const normalizedFirst = [...first].sort((a, b) => a - b);
    const normalizedSecond = [...second].sort((a, b) => a - b);

    return normalizedFirst.length === normalizedSecond.length &&
      normalizedFirst.every((value, index) => value === normalizedSecond[index]);
  }

  private setAreaEditOptions(): void {
    const editOptions = this.areaOptions.map(area => ({ value: area.id, label: area.name }));
    this.columns = this.columns.map(column =>
      column.key === 'area' ? { ...column, editOptions } : column
    );
  }

  /** Abre el formulario modal para crear un nombramiento. */
  createNew(): void {
    this.openEditorDialog({});
  }

  /** Abre el formulario modal para editar un nombramiento existente. */
  editAssignment(assignment: AssignmentDto): void {
    this.openEditorDialog({ assignment });
  }

  private openEditorDialog(data: AssignmentEditorDialogData): void {
    this.dialog.open(AssignmentEditorComponent, modalDialogConfig<AssignmentEditorDialogData>({
      data,
      size: 'md',
      layout: 'form',
    }))
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(saved => {
        if (saved) {
          this.loadAssignments();
        }
      });
  }

  /**
   * Solicita confirmaciÃ³n para eliminar un nombramiento
   */
  deleteAssignment(assignment: AssignmentDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar Nombramiento',
        message: `Â¿EstÃ¡s seguro de que deseas eliminar el nombramiento "${assignment.name}"?`
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.confirmDelete(assignment.id!);
        }
      });
  }

  /**
   * Confirma la eliminaciÃ³n de un nombramiento
   */
  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Nombramiento eliminado exitosamente');
          this.loadAssignments();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error deleting assignment:', error);
          this.notificationService.error((error.error as any)?.message || 'Error al eliminar el nombramiento');
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Maneja el reordenamiento de nombramientos
   */
  onAssignmentReorder(event: any): void {
    const { data } = event;
    
    // Actualizar el sortOrder para cada item basado en su nueva posiciÃ³n
    data.forEach((assignment: AssignmentDto, index: number) => {
      this.service.updateSortOrder(assignment.id!, index + 1)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (error: any) => {
            console.error('Error updating assignment sort order:', error);
            this.notificationService.error('Error al actualizar el orden');
            // Recargar los datos para revertir los cambios
            this.loadAssignments();
            this.cdr.markForCheck();
          }
        });
    });
    
    this.notificationService.success('Orden actualizado exitosamente');
  }

  /**
   * Maneja el cambio de estado (activo/inactivo) de un nombramiento
   */
  onStatusChange(assignment: AssignmentDto, newStatus: boolean): void {
    const updateDto = this.buildUpdateDto(assignment, { isActive: newStatus });

    this.service.update(assignment.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          assignment.isActive = newStatus;
          this.notificationService.success(
            newStatus ? 'Nombramiento activado exitosamente' : 'Nombramiento desactivado exitosamente'
          );
          this.applyFilter();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating assignment status:', error);
          this.notificationService.error('Error al actualizar el estado del nombramiento');
          // Recargar los datos para revertir el cambio
          this.loadAssignments();
          this.cdr.markForCheck();
        }
      });
  }

  onCellEditSave(event: CellEditSaveEvent<AssignmentDto>): void {
    if (!['name', 'area', 'levels', 'requiresActiveMinistry'].includes(event.column)) {
      return;
    }

    const assignment = event.row;
    const changes: Partial<UpdateAssignmentDto> = {};
    let successMessage = 'Nombramiento actualizado exitosamente';

    if (event.column === 'name') {
      const newName = String(event.newValue ?? '').trim();
      const oldName = String(event.oldValue ?? '');

      if (!newName) {
        this.notificationService.warning('El nombre del nombramiento no puede quedar vacio');
        return;
      }

      if (newName === oldName) {
        return;
      }

      changes.name = newName;
    }

    if (event.column === 'area') {
      const newAreaId = Number(event.newValue);
      const oldAreaId = Number(event.oldValue);

      if (!newAreaId) {
        this.notificationService.warning('El area del nombramiento no puede quedar vacia');
        return;
      }

      if (newAreaId === oldAreaId) {
        return;
      }

      changes.areaId = newAreaId;
    }

    if (event.column === 'requiresActiveMinistry') {
      const requiresActiveMinistry = event.newValue === true || event.newValue === 'true';

      if (requiresActiveMinistry === (event.oldValue ?? false)) {
        return;
      }

      changes.requiresActiveMinistry = requiresActiveMinistry;
      successMessage = requiresActiveMinistry
        ? 'Nombramiento actualizado: requiere ministerio activo'
        : 'Nombramiento actualizado: no requiere ministerio activo';
    }

    if (event.column === 'levels') {
      const levelIds = (Array.isArray(event.newValue) ? event.newValue : [])
        .map(value => Number(value))
        .filter(value => !Number.isNaN(value));
      const oldLevelIds = this.getAssignmentLevelIds(assignment);

      if (levelIds.length === 0) {
        this.notificationService.warning('El nombramiento debe tener al menos un nivel');
        return;
      }

      if (this.areNumberArraysEqual(levelIds, oldLevelIds)) {
        return;
      }

      changes.allowedOrganizationLevelIds = levelIds;
    }

    this.savingCell = { row: assignment, column: event.column };
    this.cdr.markForCheck();

    const updateDto = this.buildUpdateDto(assignment, changes);

    this.service.update(assignment.id!, updateDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Object.assign(assignment, changes);
          if (changes.allowedOrganizationLevelIds) {
            assignment.allowedOrganizationLevelNames = this.getLevelNames(changes.allowedOrganizationLevelIds);
            this.refreshFilterOptions();
            this.filterForm.controls.level.setValue(this.levelFilterOptions, { emitEvent: false });
          }
          this.notificationService.success(successMessage);
          this.savingCell = undefined;
          this.applyFilter();
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating assignment inline field:', error);
          this.notificationService.error('Error al actualizar el nombramiento');
          this.savingCell = undefined;
          this.loadAssignments();
          this.cdr.markForCheck();
        }
      });
  }

  private buildUpdateDto(assignment: AssignmentDto, changes: Partial<UpdateAssignmentDto> = {}): UpdateAssignmentDto {
    return {
      name: changes.name ?? assignment.name ?? '',
      areaId: changes.areaId ?? assignment.areaId ?? 0,
      sortOrder: changes.sortOrder ?? assignment.sortOrder ?? 0,
      isActive: changes.isActive ?? assignment.isActive ?? true,
      requiresActiveMinistry: changes.requiresActiveMinistry ?? assignment.requiresActiveMinistry ?? false,
      allowedOrganizationLevelIds: changes.allowedOrganizationLevelIds ?? assignment.allowedOrganizationLevelIds ?? [],
    };
  }

  onToolbarSearch(term: string): void {
    this.filterForm.get('searchTerm')?.setValue(term);
  }

  onFilterRemove(key: string): void {
    switch (key) {
      case 'status':
        this.filterForm.controls.status.setValue(this.statusFilterOptions.map(option => option.value), { emitEvent: false });
        break;
      case 'areaId':
        this.filterForm.controls.areaId.setValue(this.areaSelectOptions.map(option => option.value), { emitEvent: false });
        break;
      case 'level':
        this.filterForm.controls.level.setValue(this.levelFilterOptions, { emitEvent: false });
        break;
      case 'requiresActiveMinistry':
        this.filterForm.controls.requiresActiveMinistry.setValue(this.ministryFilterOptions.map(option => option.value), { emitEvent: false });
        break;
      default:
        return;
    }
    this.applyFilter();
  }

  private loadAreas(): void {
    this.areaService.getActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: areas => {
          this.areaOptions = areas;
          this.setAreaEditOptions();
          this.cdr.markForCheck();
        },
        error: () => this.notificationService.error('No se pudieron cargar las áreas'),
      });
  }
}

